import { useEffect, useReducer, useState } from 'react'

import {
  getShip,
  getShips,
  getContracts,
  getAgent,
  getSystem,
  getShipCooldown,
  navigate,
  orbitShip,
  dockShip,
  refuelShip,
  jumpShip,
  updateFlightMode,
  getJumpGate,
  doSurvey,
  doExtraction,
  getMarket,
  getShipyard,
  negotiateContract,
  accept,
  fulfill,
  purchaseCargo,
  sellCargo,
  purchaseShip,
  deliver,
  shipJettison,
  shipRefine,
  getWaypoint,
  uninstallMount,
  installMount,
  scanSystems,
  registerWithNameAndFaction,
  clearDb,
} from './api_mutex';

import { format } from './helpers';
import useMessages from './useMessages';

function useStore() {

  const defaultState = {
    token: null,
    agent: null,
    ships: null,
    system: null,
    waypoint: null,
    surveys: {},
    market: null,
    shipyard: null,
    gates: null,
    marketplace: null,
    autopilot: null,
    contracts: [],
    darkmode: true,
    loading: false,
    systems: [],
  };

  // Our stored workers
  const [workers, setWorkers] = useState({});

  // Use a reducer for state changes
  const [state, dispatch] = useReducer((state, action) => ({...action}), defaultState);

  // Use messages
  const messages = useMessages();

  // On load, load state from storage
  useEffect(() => {

    const state = localStorage.getItem('state');
    if (state === null) {
      dispatch(defaultState);
    } else {
      let newState = JSON.parse(state);
      if (newState.token) {
        newState.loading = true;
      } else {
        newState.loading = false;
      }

      if (newState.autopilot) {
        for (let key of Object.keys(newState.autopilot)) {
          let pilot = {...newState.autopilot[key]};
          pilot.worker = undefined;
          newState.autopilot[key] = pilot;
        }
      }

      clearDb();
      dispatch({...newState});

      // Also reload ships
      if (newState.token) {
        getShipsWithCooldown(newState.token)
          .then((response) => {
            if (response.error) {
              window.alert(response.error.message);
            } else {
              getContracts('main', newState.token)
                .then((contractsResponse) => {
                  newState.ships = response;
                  newState.contracts = contractsResponse.data;
                  newState.loading = false;
                  dispatch({...newState});
               });
            }
          });
      }
    }

  }, []);

  // When state changes, store into storage
  useEffect(() => {
    if (state !== defaultState) {
      // Store new state
      localStorage.setItem('state', JSON.stringify(state));
    }
  }, [state]);

  function getStateFromStorage() {
    const state = localStorage.getItem('state');
    return JSON.parse(state);
  }

  function attachShipsToWaypoints(ships, waypoints) {
    for (let waypoint of waypoints) {
      waypoint.ships = ships
        .filter((s) => s.nav.waypointSymbol == waypoint.symbol)
        .map((s) => s.symbol);
    }
  }

  async function register(name, faction) {
    let newState = {...getStateFromStorage()};
    newState.loading = true;
    dispatch({...newState});

    const result = await registerWithNameAndFaction('main', name, faction);
    if (result.error) {
      let newState = {...getStateFromStorage()};
      newState.loading = false;
      dispatch({...newState});
      throw new Error(result.error.message);
    }

    const token = result.data.token;
    const agent = result.data.agent;
    const ships = await getShips('main', token);
    const systemSymbol = ships.data[0].nav.systemSymbol;
    const system = await getSystem('main', token, systemSymbol);
    const contracts = await getContracts('main', token);

    newState = {...getStateFromStorage()};
    attachShipsToWaypoints(ships.data, system.data.waypoints);
    newState.agent = agent;
    newState.token = token;
    newState.ships = ships.data;
    newState.system = system.data;
    newState.contracts = contracts.data;
    newState.loading = false;
    dispatch({...newState});
  }

  async function login(token) {
    let newState = {...getStateFromStorage()};
    newState.loading = true;
    dispatch({...newState});

    let agent;
    try {
      agent = await getAgent('main', token);
    } catch (e) {
      let newState = {...getStateFromStorage()};
      newState.loading = false;
      dispatch({...newState});
      throw new Error(e.message);
    }

    const ships = await getShips('main', token);
    const systemSymbol = ships.data[0].nav.systemSymbol;
    const system = await getSystem('main', token, systemSymbol);
    const contracts = await getContracts('main', token);
    newState = {...getStateFromStorage()};

    attachShipsToWaypoints(ships.data, system.data.waypoints);
    newState.agent = agent.data;
    newState.token = token;
    newState.ships = ships.data;
    newState.system = system.data;
    newState.contracts = contracts.data;
    newState.loading = false;
    dispatch({...newState});
  }

  function setWaypoint(waypoint) {
    let newState = {...getStateFromStorage()};
    newState.waypoint = waypoint;
    dispatch({...newState});
  }

  async function setWaypointAndCloseModal(waypoint) {
    let newState = {...getStateFromStorage()};
    const response = await getWaypoint('main', state.token, waypoint)

    if (!response.error) {
      newState.waypoint = response.data;
      newState.modal = false;
      dispatch({...newState});
    } else {
      window.alert(response.error.message);
    }
  }

  function setDarkmode(darkmode) {
    let newState = {...getStateFromStorage()};
    newState.darkmode = darkmode;
    dispatch({...newState});
  }

  function setModal(modal, tab) {
    let newState = {...getStateFromStorage()};
    newState.modal = {
      modal,
      tab,
    };
    return dispatch({...newState});
  }

  function getSelectedShip(shipSymbol) {
    if (shipSymbol) {
      return state.ships.find((s) => s.symbol == shipSymbol);
    }

    if (!state.selectedShip) {
      return undefined;
    }
    return state.ships.find((s) => s.symbol == state.selectedShip);
  }

  function setSurvey(newState, ship, survey) {
    newState.surveys[survey.symbol] = survey;
    return newState;
  }

  function getSurvey(symbol) {
    let newState = {...getStateFromStorage()};
    if (!newState.surveys) {
      return null;
    }

    const survey = newState.surveys[symbol];
    if (!survey) {
      return null;
    }

    const expiration = new Date(survey.expiration);
    const validity = expiration.valueOf() - (new Date()).valueOf();

    // Only return survey if still valid for more than a second
    if (validity < 1000) {
      return null;
    }

    return survey;
  }

  function getConnectedSystems(symbol) {
    if (state.gates === null || state.gates === undefined) {
      return null;
    }

    if (state.gates[symbol]) {
      return state.gates[symbol];
    }

    return null;
  }

  /**
   * Select a new ship, and fetch the new system if needed
   */
  async function selectShip(shipSymbol) {
    if (state.system.symbol !== getSelectedShip(shipSymbol).nav.systemSymbol) {
      const response = await getSystem('main', state.token, getSelectedShip(shipSymbol).nav.systemSymbol);
      if (!response.error) {
        let newState = {...getStateFromStorage()};
        newState.selectedShip = shipSymbol;
        newState.system = response.data;
        attachShipsToWaypoints(newState.ships, newState.system.waypoints);
        dispatch({...newState});
      } else {
        window.alert(response.error.message);
      }
    } else {
      let newState = {...getStateFromStorage()};
      newState.selectedShip = shipSymbol;
      dispatch({...newState});
    }
  }

  /**
   * Navigate the given ship to the given waypoint
   */
  async function navigateTo(shipSymbol, waypointSymbol) {
    let newState = {...getStateFromStorage()};
    const response = await navigate('main', state.token, shipSymbol, waypointSymbol);
    if (!response.error) {
      const nav = response.data.nav;
      const fuel = response.data.fuel;
      let ship = getSelectedShip(shipSymbol);
      ship.nav = nav;
      ship.fuel = fuel;

      // Set duration
      const arrival = new Date(nav.route.arrival);
      const seconds = arrival.valueOf() - (new Date()).valueOf();
      ship.action = {
        duration: Math.ceil(seconds / 1000),
        type: 'NAVIGATE',
      };
      newState.selectedShip = ship.symbol;
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${state.selectedShip} sets course for ${waypointSymbol} in ${nav.flightMode} mode, expected arrival time is ${format(arrival)}`,
      });
      dispatch({...newState});

      // When arrived reload ship
      setTimeout(async () => {
        await reloadShip(shipSymbol);
      }, seconds);

    } else {
      window.alert(response.error.message);
    }
  }

  /**
   * Move ship to orbit
   */
  async function orbit(shipSymbol) {
    const response = await orbitShip('main', state.token, shipSymbol);
    if (!response.error) {
      let newState = {...getStateFromStorage()};
      const nav = response.data.nav;
      let ship = getSelectedShip(shipSymbol);
      ship.nav = nav;
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      newState.selectedShip = ship.symbol;

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${ship.symbol} is now in orbit`,
      });

      dispatch({...newState});
    } else {
      window.alert(response.error.message);
    }
  }

  /**
   * Dock ship at the current waypoint
   */
  async function dock(shipSymbol) {
    const response = await dockShip('main', state.token, shipSymbol);
    if (!response.error) {

      let newState = {...getStateFromStorage()};
      const nav = response.data.nav;
      let ship = getSelectedShip(shipSymbol);
      ship.nav = nav;
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      newState.selectedShip = ship.symbol;

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${ship.symbol} is now docked`,
      });

      dispatch({...newState});
    } else {
      window.alert(response.error.message);
    }
  }

  /**
   * Reload all the ships, and handle navigation and cooldown
   */
  async function getShipsWithCooldown(token) {
    const ships = await getShips('main', token);
    if (ships.error) {
      throw new Error(ships.error.message);
    }

    let result = [];
    for (let ship of ships.data) {

      // Check if navigating
      if (ship.nav.status == 'IN_TRANSIT') {
        const arrival = new Date(ship.nav.route.arrival);
        const seconds = arrival.valueOf() - (new Date()).valueOf();
        const duration = Math.ceil(seconds / 1000);

        ship.action = {
          duration: duration,
          type: 'NAVIGATE',
        };

        // When arrived reload ship
        setTimeout(async () => {
          await reloadShipWithToken(token, ship.symbol);
        }, duration * 1000);
      } else {

        let cooldown = await getShipCooldown('main', token, ship.symbol);
        if (cooldown.data) {
          ship.action = {
            duration: cooldown.data.remainingSeconds,
            type: 'EXTRACT',
          };

          // When arrived reload ship
          setTimeout(async () => {
            await reloadShipWithToken(token, ship.symbol);
          }, cooldown.data.remainingSeconds * 1000);
        }
      }

      result.push(ship);
    }

    return result;
  }

  /**
   * Reload all ships from the API
   */
  async function reloadShip(symbol, newState = null) {
    const response = await getShip('main', state.token, symbol);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      if (newState === null) {
        newState = {...getStateFromStorage()};
      }
      newState.ships = newState.ships.map((ship) => {
        if (ship.symbol == symbol) {
          return response.data;
        } else {
          return ship;
        }
      });
      attachShipsToWaypoints(newState.ships, newState.system.waypoints);
      dispatch({...newState});
    }
  }

  /**
   * Reload all ships from the API
   */
  async function reloadShipWithToken(token, symbol) {
    const response = await getShip('main', token, symbol);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.ships = newState.ships.map((ship) => {
        if (ship.symbol == symbol) {
          return response.data;
        } else {
          return ship;
        }
      });
      attachShipsToWaypoints(newState.ships, newState.system.waypoints);
      dispatch({...newState});
    }
  }

  async function updateShip(ship) {
    let newState = {...getStateFromStorage()};
    newState.ships = newState.ships.map((sh) => {
      if (sh.symbol == ship.symbol) {
        return ship;
      } else {
        return sh;
      }
    });
    dispatch({...newState});
  }

  async function updateAgent({ agent, ship }) {
    let newState = {...getStateFromStorage()};
    newState.ships = newState.ships.map((sh) => {
      if (sh.symbol == ship.symbol) {
        return ship;
      } else {
        return sh;
      }
    });
    newState.agent = agent;
    dispatch({...newState});
  }

  async function updateContract(contract) {
    let newState = {...getStateFromStorage()};
    newState.contracts = newState.contracts.map((c) => {
      if (c.id == contract.id) {
        return contract;
      } else {
        return c;
      }
    });
    dispatch({...newState});
  }

  /**
   * Update the flight mode for the selected ship
   */
  async function setFlightMode(mode) {
    let newState = {...getStateFromStorage()};
    const response = await updateFlightMode('main', state.token, state.selectedShip, mode);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let ship = getSelectedShip();
      ship.nav = response.data;
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      newState.selectedShip = ship.symbol;

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${ship.symbol} now flies on ${ship.nav.flightMode}`,
      });

      dispatch({...newState});
    }
  }

  /**
   * Refuel the selected ship
   */
  async function refuel(waypoint) {
    let newState = {...getStateFromStorage()};
    const response = await refuelShip('main', state.token, state.selectedShip);
    console.log(response);

    if (response.error) {
      window.alert(response.error.message);
    } else {
      newState.agent = response.data.agent;
      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${state.selectedShip} is refilled by ${response.data.transaction.units} units of fuel at a cost of ${response.data.transaction.totalPrice}`,
      });
      await reloadShip(state.selectedShip, newState);
    }
  }

  /**
   * Scan the jumpgate
   */
  async function scanJumpGate(waypoint) {
    let newState = {...getStateFromStorage()};
    const response = await getJumpGate('main', state.token, state.system.symbol, state.waypoint.symbol);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      if (newState.gates === null || newState.gates === undefined) {
        newState.gates = {};
      }
      newState.gates[waypoint.symbol] = response.data;
      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `There are ${response.data.connectedSystems.length} connected systems discoverd on ${waypoint.symbol}`,
      });
      dispatch({...newState});
    }
  }

  /**
   * Jump to the first available system for this jumpgate
   */
  async function jump(systemSymbol) {
    let newState = {...getStateFromStorage()};
    const resp = await jumpShip('main', state.token, state.selectedShip, systemSymbol);
    if (resp.error) {
      window.alert(resp.error.message);
    } else {

      // Set duration
      let ship = getSelectedShip();
      ship.nav = resp.data.nav;
      ship.action = {
        duration: resp.data.cooldown.remainingSeconds,
        type: 'JUMP',
      };
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      newState.selectedShip = ship.symbol;

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${ship.symbol} is jumped to ${resp.data.nav.systemSymbol}`,
      });

      dispatch({...newState});

      // When cooldown done, reload ships
      setTimeout(async () => {
        await reloadShip(state.selectedShip);
      }, ship.action.duration * 1000);
    }
  }

  /**
   * Survey an asteroid to locate better resources
   */
  async function survey() {
    const response = await doSurvey('main', state.token, state.selectedShip);
    if (response.error) {
      window.alert(response.error.message);
    } else {

      // Set duration and store survey
      let newState = {...getStateFromStorage()};
      let ship = getSelectedShip();
      ship.action = {
        duration: response.data.cooldown.remainingSeconds,
        type: 'SURVEY',
      };
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      newState = setSurvey(newState, ship, response.data.surveys[0]);

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${ship.symbol} 1 has been surveying the planet`,
      });

      dispatch({...newState});

      // When cooldown done, reload ships
      setTimeout(async () => {
        await reloadShip(state.selectedShip);
      }, ship.action.duration * 1000);
    }
  }

  // Extract resources, then wait for cooldown
  async function extract() {
    const response = await doExtraction('main', state.token, state.selectedShip, getSurvey(state.waypoint.symbol));
    if (response.error) {
      messages.add({
        timestamp: Date.now(),
        ship: state.selectedShip,
        messageType: 'error',
        message: response.error.message
      });
    } else {

      // Set duration and store survey
      let newState = {...getStateFromStorage()};
      let ship = getSelectedShip();
      ship.action = {
        duration: response.data.cooldown.remainingSeconds,
        type: 'EXTRACT',
      }
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      // TODO: Update ships
      dispatch({...newState});
      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${state.selectedShip} is extracting ${response.data.extraction.yield.symbol} from ${state.waypoint.symbol}`
      });

      // When cooldown done, reload ships
      setTimeout(async () => {
        await reloadShip(state.selectedShip);
      }, ship.action.duration * 1000);
    }
  }

  /**
   * Fetch information about a marketplace
   */
  async function fetchMarket() {
    const response = await getMarket('main', state.token, state.system.symbol, state.waypoint.symbol);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.marketplace = response.data;
      newState.modal = {modal: 'marketplace'};
      return dispatch({...newState});
    }
  }

  /**
   * Fetch information about a shipyard
   */
  async function fetchShipyard() {
    const response = await getShipyard('main', state.token, state.system.symbol, state.waypoint.symbol);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.shipyard = response.data;
      newState.modal = {modal: 'shipyard'};

      return dispatch({...newState});
    }
  }

  /**
   * Fetch information about a shipyard
   */
  async function getContract() {
    let newState = {...getStateFromStorage()};
    const response = await negotiateContract('main', newState.token, newState.selectedShip);
    if (response.error) {
      window.alert(response.error.message);
    } else {

      // Store the new contract
      if (newState.contracts === null || newState.constract === undefined) {
        newState.contracts = [];
      }
      newState.contracts.push(response.data.contract);
      dispatch({...newState});
    }
  }

  /**
   * Accept the given contract.
   */
  async function acceptContract(contractId) {
    const response = await accept('main', state.token, contractId);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.agent = response.data.agent;
      newState.contracts = [];
      newState.contracts.push(response.data.contract);
      dispatch({...newState});
    }
  }

  /**
   * Fulfill the given contract.
   */
  async function fulfillContract(contractId) {
    const response = await fulfill('main', state.token, contractId);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.agent = response.data.agent;
      newState.contracts = [];
      newState.contracts.push(response.data.contract);
      dispatch({...newState});
    }
  }

  /**
   * Purchase cargo
   */
  async function purchase(symbol, amount) {
    const response = await purchaseCargo('main', state.token, state.selectedShip, symbol, amount);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.agent = response.data.agent;
      await reloadShip(state.selectedShip, newState);
    }
  }

  /**
   * Sell cargo
   */
  async function sell(symbol, amount) {
    const response = await sellCargo('main', state.token, state.selectedShip, symbol, amount);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.agent = response.data.agent;

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: 'Cargo has been sold',
      });

      await reloadShip(state.selectedShip, newState);
    }
  }

  /**
   * Purchase a new ship of the given type
   */
  async function purchaseNewShip(type) {
    const response = await purchaseShip('main', state.token, state.waypoint.symbol, type);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.agent = response.data.agent;
      newState.ships.push(response.data.ship);
      attachShipsToWaypoints(newState.ships, newState.system.waypoints);
      newState.agent = response.data.agent;
      dispatch({...newState});
    }
  }

  async function refine(produce) {
    let newState = {...getStateFromStorage()};
    const response = await shipRefine('main', newState.token, state.selectedShip, produce);
    if (response.error) {
      window.alert(response.error.message);
    } else {

      // Set duration
      let ship = getSelectedShip();
      ship.action = {
        duration: response.data.cooldown.remainingSeconds,
        type: 'REFINE',
      };
      newState.selectedShip = ship.symbol;
      dispatch({...newState});

      // When cooldown done, reload ships
      setTimeout(async () => {
        await reloadShip(state.selectedShip);
      }, ship.action.duration * 1000);
    }
  }

  /**
   * Jettison some cargo
   */
  async function jettison(symbol, units) {
    let newState = {...getStateFromStorage()};
    const response = await shipJettison('main', newState.token, state.selectedShip, symbol, units);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      await reloadShip(state.selectedShip);
    }
  }

  /**
   * Deliver any cargo the selected ship has
   */
  async function deliverCargo() {
    const ship = getSelectedShip();
    const contracts = state.contracts.filter((c) => c.accepted && !c.fulfilled);
    const contract = contracts[0];
    for (let item of ship.cargo.inventory) {
      const tradeSymbol = contract.terms.deliver[0].tradeSymbol;
      if (item.symbol == tradeSymbol) {
        const response = await deliver(
          'main',
          state.token,
          contract.id,
          ship.symbol,
          tradeSymbol,
          item.units,
        );

        messages.add({
          timestamp: Date.now(),
          messageType: 'success',
          message: `${ship.symbol} has delivered ${item.units} units of ${tradeSymbol} on ${ship.nav.waypointSymbol}`,
        });

        if (response.error) {
          window.alert(response.error.message);
        }
      }
    }

    let newState = {...getStateFromStorage()};
    getShip('main', newState.token, state.selectedShip)
      .then((response) => {
        if (response.error) {
          window.alert(response.error.message);
        } else {
          getContracts('main', newState.token)
            .then((contractsResponse) => {
              newState.ships = newState.ships.map((ship) => {
                if (ship.symbol == state.selectedShip) {
                  return response.data;
                } else {
                  return ship;
                }
              });
              newState.contracts = contractsResponse.data;
              dispatch({...newState});
           });
        }
      });
  }

  /**
   * Sell everything in the cargo hold
   */
  async function sellAll() {
    const ship = getSelectedShip();
    const items = ship.cargo.inventory;

    if (items.length > 0) {
      for (let item of items) {
        let response = await sellCargo(state.token, ship.symbol, item.symbol, item.units);
        if (response.error) {
          window.alert(response.error.message);
          break;
        }
      }
    }

    // Reload ship and agent
    const response = await getAgent('main', state.token);
    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      newState.agent = response.data;

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: `${state.selectedShip} selled all cargo`,
      });

      await reloadShip(state.selectedShip, newState);
    }
  }

  /**
   * Save setting for the autopilot for a specific ship
   */
  function saveAutopilot(shipSymbol, data) {
    let newState = {...getStateFromStorage()};
    if (!newState.autopilot) {
      newState.autopilot = {};
    }
    newState.autopilot[shipSymbol] = data;
    dispatch({...newState});
  }

  /**
   * Start of stop the autopilot for the given ship
   */
  async function toggleAutopilot(shipSymbol) {
    let newState = {...getStateFromStorage()};
    const program = newState.autopilot[shipSymbol];
    const ship = newState.ships.find((s) => s.symbol == shipSymbol);
    const index = ship.symbol;

    // Start worker
    if (program.worker === undefined) {
      console.log('Start');
      newState.autopilot[shipSymbol].worker = index;
      const worker = new Worker("workers/worker.js");

      worker.postMessage({
        type: 'init',
        token: newState.token,
        ship: ship,
        program: program,
        index: index,
        contractId: newState.contracts.length > 0 ? newState.contracts[0].id : null,
      });

      worker.onmessage = (e) => {
        if (e.data.type == 'MESSAGE') {
          messages.add({
            timestamp: Date.now(),
            messageType: 'success',
            message: e.data.message,
          });
        }
        if (e.data.type == 'SHIP') {
          console.log('ship update', e.data.ship);
          updateShip(e.data.ship);
        }
        if (e.data.type == 'AGENT') {
          console.log('ship and agent update', e.data);
          updateAgent(e.data);
        }
        if (e.data.type == 'CONTRACT') {
          console.log('contract update', e.data);
          updateContract(e.data.contract);
        }
        if (e.data.type == 'CLOSE') {
          let newWorkers = {...workers};
          newState.autopilot[shipSymbol].worker = undefined;
          //newWorkers = Object.entries(newWorkers).filter((w) => w != worker);
          delete newWorkers[index];
          setWorkers(newWorkers);
          console.log('Cleanup worker', newWorkers);
        }
      };

      const newWorkers = {...workers};
      newWorkers[index] = worker;
      setWorkers(newWorkers);
      dispatch({...newState});

    // Stop worker
    } else {
      console.log('stop');
      //const index = newState.autopilot[shipSymbol].worker;
      if (workers[index]) {
        workers[index].postMessage({ type: 'stop' });
      }
    }
  }

  /**
   * Logout: clear session
   */
  function logout() {
    dispatch({...defaultState});
  }

  /**
   * Uninstall a mount. Note: must be done while docked at a shipyard.
   * Also costs a bit of money.
   */
  async function uninstall(mount) {
    const response = await uninstallMount('main', state.token, state.selectedShip, mount);
    if (response.error) {
      window.alert(response.error.message);
    } else {

      const cargo = response.data.cargo;
      const mounts = response.data.mounts;
      const agent = response.data.agent;

      let newState = {...getStateFromStorage()};
      newState.agent = agent;
      let ship = getSelectedShip();
      ship.cargo = cargo;
      ship.mounts = mounts;
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      dispatch({...newState});
    }
  }

  /**
   * Install a mount from the cargo space
   */
  async function install(mount) {
    const response = await installMount('main', state.token, state.selectedShip, mount);
    if (response.error) {
      window.alert(response.error.message);
    } else {

      const cargo = response.data.cargo;
      const mounts = response.data.mounts;
      const agent = response.data.agent;

      let newState = {...getStateFromStorage()};
      newState.agent = agent;
      let ship = getSelectedShip();
      ship.cargo = cargo;
      ship.mounts = mounts;
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      dispatch({...newState});
    }
  }

  /**
   * Scan nearby systems
   */
  async function shipScanSystems() {
    if (state.ships.length < 1) {
      return;
    }

    let ship = getSelectedShip();
    if (!ship) {
      ship = state.ships[0];
    }
    const response = await scanSystems('main', state.token, ship.symbol);

    if (response.error) {
      window.alert(response.error.message);
    } else {
      let newState = {...getStateFromStorage()};
      ship.action = {
        duration: response.data.cooldown.remainingSeconds,
        type: 'SCAN_SYSTEMS',
      };
      newState.ships = newState.ships.map((s) => s.symbol == ship.symbol ? ship : s);
      newState.systems = response.data.systems;

      messages.add({
        timestamp: Date.now(),
        messageType: 'success',
        message: 'Nearby systems are scanned',
      });

      dispatch({...newState});

      setTimeout(async () => {
        await reloadShip(ship);
      }, ship.action.duration * 1000);
    }
  }

  return {
    state,
    messages,
    setWaypoint,
    setWaypointAndCloseModal,
    selectShip,
    navigateTo,
    setDarkmode,
    orbit,
    dock,
    logout,
    getSelectedShip,
    setFlightMode,
    refuel,
    jump,
    survey,
    extract,
    fetchMarket,
    fetchShipyard,
    setModal,
    scanJumpGate,
    getConnectedSystems,
    getContract,
    acceptContract,
    fulfillContract,
    purchase,
    sell,
    purchaseNewShip,
    deliverCargo,
    jettison,
    refine,
    sellAll,
    getSurvey,
    saveAutopilot,
    toggleAutopilot,
    login,
    register,
    uninstall,
    install,
    shipScanSystems,
  }
};

export default useStore;
